import { ReactNode, useMemo } from 'react'
import { MenuTitles, Pages } from 'constants/menu'
import {
  AgxDivider,
  AgxEmptyState,
  AgxSaveChangesButton,
  AgxSpacer,
  AgxToggleSwitch,
  AgxTypography,
  SAVE_BUTTON_STATES,
} from '@urbanx/agx.ui-components2'
import LeftSubmenu, { SubPage } from 'components/left-submenu/LeftSubmenu'
import { useParams } from 'react-router-dom'
import { useSelectedCampaign } from 'hooks'
import { useStaffGetPropertyInformationProgressForCampaign } from '@urbanx/agx.campaigns.client'
import { FormPrompt } from 'components/FormPrompt'

interface PropertyInformationSubPageProps {
  currentPage: Pages
  isLoading: boolean
  saveState: (typeof SAVE_BUTTON_STATES)[keyof typeof SAVE_BUTTON_STATES]
  hasUnsavedChanges: boolean
  onSave: () => void
  saveButtonId: string
  saveButtonTestId: string
  showChildren: boolean
  children: ReactNode
}

const PropertyInformationSubPage = ({
  currentPage,
  isLoading,
  saveState,
  hasUnsavedChanges,
  onSave,
  saveButtonId,
  saveButtonTestId,
  children,
}: PropertyInformationSubPageProps) => {
  const { campaignId } = useParams()
  const { campaign } = useSelectedCampaign()
  const blankContractIsLive = campaign?.blankContractIsLive ?? false
  const {
    data: progressData,
    isPending,
    isError,
  } = useStaffGetPropertyInformationProgressForCampaign(
    campaignId ?? '',
    !!campaignId
  )

  const getStatus = (key: string) => {
    const value = progressData?.campaignSectionPrepared?.[key]
    if (typeof value !== 'boolean') return undefined
    return value ? 'success' : 'error'
  }

  const subPages = useMemo<SubPage[]>(
    () => [
      {
        title: MenuTitles.PROPERTY_DETAILS,
        link: Pages.PROPERTY_INFORMATION_PROPERTY_DETAILS,
        statusIndicator:
          isPending || isError ? undefined : getStatus('PropertyDetails'),
      },
      {
        title: MenuTitles.VENDOR_DETAILS,
        link: Pages.PROPERTY_INFORMATION_VENDOR_DETAILS,
        statusIndicator:
          isPending || isError ? undefined : getStatus('VendorDetails'),
      },
      {
        title: MenuTitles.CONTRACT,
        link: Pages.PROPERTY_INFORMATION_CONTRACT,
        statusIndicator:
          isPending || isError ? undefined : getStatus('Contract'),
      },
      {
        title: MenuTitles.TENANCY_INFORMATION,
        link: Pages.PROPERTY_INFORMATION_TENANCY_INFORMATION,
        statusIndicator:
          isPending || isError ? undefined : getStatus('TenancyInformation'),
      },
    ],
    [getStatus, isError, isPending]
  )

  return (
    <AgxSpacer direction="vertical" gap="spacing-2xl" fill>
      <FormPrompt hasUnsavedChanges={hasUnsavedChanges} />
      <AgxSpacer
        id="subPageHeading"
        direction="horizontal"
        gap="spacing-lg"
        fill
        contentAlignment="center"
        contentJustification="space-between"
      >
        <AgxSpacer
          direction="horizontal"
          contentAlignment="start"
          contentJustification="start"
        >
          <AgxTypography
            id="pageTitle-Property-Information"
            type="body"
            size="large"
          >
            {MenuTitles.PROPERTY_INFORMATION}
          </AgxTypography>
        </AgxSpacer>
        <AgxSpacer
          direction="horizontal"
          gap="spacing-xl"
          contentAlignment="center"
          contentJustification="end"
        >
          <AgxToggleSwitch
            id="toggleContractLive"
            checked={blankContractIsLive}
            offLabel="Offline"
            onLabel="Contract Live"
            onChange={() => undefined}
          />
          <AgxSaveChangesButton
            key={saveButtonTestId}
            id={saveButtonId}
            dataTestId={saveButtonTestId}
            text="Save Changes"
            size="medium"
            variant="primary"
            state={saveState}
            disabled={!hasUnsavedChanges}
            onClick={onSave}
          />
        </AgxSpacer>
      </AgxSpacer>
      <div className="workflowTask-divider-stretch">
        <AgxDivider variant="horizontal" />
      </div>
      <AgxSpacer direction="horizontal" gap="spacing-md" fill>
        <LeftSubmenu currentPage={currentPage} subPages={subPages} />
        <AgxSpacer direction="vertical" gap="spacing-xl" fill>
          {isPending || isLoading ? (
            <AgxEmptyState variant="minimal" label="Loading..." />
          ) : (
            children
          )}
        </AgxSpacer>
      </AgxSpacer>
    </AgxSpacer>
  )
}

export default PropertyInformationSubPage
